import React from 'react';
import { Shield, Award, CheckCircle, Users, Clock, DollarSign, MapPin, Phone, Mail, Star } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const trustItems = [
    {
      icon: Shield,
      title: "Licensed & Insured",
      description: "Fully certified professionals you can trust",
      color: "blue"
    },
    {
      icon: Award,
      title: "Satisfaction Guaranteed",
      description: "100% customer satisfaction promise",
      color: "green"
    },
    {
      icon: CheckCircle,
      title: "Warranty Protected",
      description: "All work comes with comprehensive warranty",
      color: "blue"
    },
    {
      icon: Users,
      title: "Serving All Families",
      description: "From young families to seniors, we're here for everyone",
      color: "green"
    },
    {
      icon: Clock,
      title: "24/7 Emergency Service",
      description: "Always available when you need us most",
      color: "blue"
    },
    {
      icon: DollarSign,
      title: "Fair & Transparent Pricing",
      description: "Honest quotes with no hidden fees",
      color: "green"
    },
    {
      icon: Clock,
      title: "Same Day Service",
      description: "Fast response for urgent repairs",
      color: "blue"
    },
    {
      icon: CheckCircle,
      title: "Free Estimates",
      description: "No-obligation quotes on all work",
      color: "green"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-3 bg-green-600 px-6 py-3 rounded-full text-lg font-bold mb-6 shadow-xl">
              <Award size={24} />
              <span>Local Family Owned Business</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Why Choose 306 Garage Doors?
            </h1>
            <p className="text-2xl text-blue-100 leading-relaxed">
              Saskatchewan's trusted choice for premium quality garage door services
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Story</h2>
            
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-gray-200 space-y-6">
              <p className="text-xl text-gray-700 leading-relaxed">
                <span className="font-bold text-blue-800">306 Garage Doors</span> is your trusted local choice for homeowners and businesses throughout Saskatchewan. We've built our reputation on <span className="font-semibold text-green-700">quality workmanship, premium products, honest pricing, and exceptional customer service</span>.
              </p>
              
              <p className="text-xl text-gray-700 leading-relaxed">
                Whether you need a new garage door installed, an emergency repair at 3 AM, or routine maintenance to keep your door running smoothly, our team of <span className="font-semibold">licensed and insured professionals</span> is here to help. We work with all major brands and use only the best quality products for every installation.
              </p>
              
              <p className="text-xl text-gray-700 leading-relaxed">
                What sets us apart is our commitment to excellence. We provide the <span className="font-semibold text-blue-800">best workmanship using premium quality garage doors and parts</span>. We don't cut corners - we deliver superior results that last. That's why so many Saskatchewan residents trust us with their garage door needs.
              </p>

              <div className="bg-gradient-to-r from-blue-100 to-green-100 p-6 rounded-2xl border-2 border-blue-200">
                <p className="text-xl font-bold text-gray-900">
                  ✓ We're not just a business - we're your neighbors, committed to serving our community with integrity and care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">What Makes Us Different</h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            We've built our reputation on honesty, quality, and exceptional service
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {trustItems.map((item, index) => {
              const IconComponent = item.icon;
              const colorClass = item.color === 'blue' 
                ? 'from-blue-600 to-blue-700' 
                : 'from-green-600 to-green-700';

              return (
                <Card 
                  key={index}
                  className="border-2 hover:border-blue-600 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50"
                >
                  <CardHeader className="text-center">
                    <div className={`flex items-center justify-center w-20 h-20 bg-gradient-to-br ${colorClass} rounded-2xl mb-4 mx-auto shadow-xl`}>
                      <IconComponent size={40} className="text-white" />
                    </div>
                    <CardTitle className="text-xl mb-2 font-bold">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600 text-base font-medium">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-gradient-to-r from-blue-900 to-blue-800 rounded-3xl p-12 shadow-2xl">
            <h2 className="text-4xl font-bold text-white text-center mb-12">Our Track Record</h2>
            <div className="grid sm:grid-cols-3 gap-8 text-center text-white">
              <div>
                <div className="text-6xl font-black mb-2">1000+</div>
                <div className="text-xl font-semibold text-blue-200">Happy Customers</div>
                <p className="text-blue-300 mt-2">Across Saskatchewan</p>
              </div>
              <div>
                <div className="text-6xl font-black mb-2">24/7</div>
                <div className="text-xl font-semibold text-blue-200">Always Available</div>
                <p className="text-blue-300 mt-2">Emergency Service</p>
              </div>
              <div>
                <div className="text-6xl font-black mb-2">100%</div>
                <div className="text-xl font-semibold text-blue-200">Satisfaction Rate</div>
                <p className="text-blue-300 mt-2">Guaranteed Quality</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Testimonial */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">What Our Customers Say</h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Real feedback from Saskatchewan families we've helped
          </p>

          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-gray-200">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_trusted-garage-pro/artifacts/yhs02hfw_WhatsApp%20Image%202026-04-14%20at%2010.53.26%20PM.jpeg"
                    alt="Customer thank you note"
                    className="rounded-2xl shadow-xl w-full border-4 border-gray-100"
                  />
                </div>

                <div className="space-y-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={28} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  <blockquote className="text-xl text-gray-800 leading-relaxed italic">
                    "Thanks so much for helping this Sr. Citizen with your emergency service. You're great. 
                    Please have a meal on me to say thanks!"
                  </blockquote>

                  <div className="pt-4 border-t-2 border-gray-200">
                    <p className="font-bold text-gray-900 text-lg">Sonja Wood</p>
                    <p className="text-gray-600">Saskatchewan Resident</p>
                    <p className="text-sm text-green-700 font-semibold mt-1">Emergency Service Customer</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Proudly Serving Saskatchewan</h2>
            <div className="bg-gradient-to-r from-blue-100 to-green-100 rounded-3xl p-8 border-2 border-blue-200">
              <div className="flex items-center justify-center gap-3 mb-4">
                <MapPin size={32} className="text-blue-800" />
                <p className="text-2xl font-bold text-gray-900">Local • Trusted • Reliable</p>
              </div>
              <p className="text-xl text-gray-700 leading-relaxed">
                We serve all of Saskatchewan and surrounding areas. As a local family-owned business, 
                we understand the needs of our community and are committed to providing the best service possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl font-bold mb-6">Ready to Experience the Difference?</h2>
            <p className="text-2xl text-blue-100 mb-8">
              Call us today for a free quote or emergency service
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
              <div className="bg-white text-gray-900 rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-2">
                  <Phone size={24} className="text-blue-800" />
                  <p className="text-sm font-semibold text-gray-600">CALL US NOW</p>
                </div>
                <a href="tel:306-262-5995" className="text-4xl font-black text-blue-800 hover:text-blue-900">
                  306-262-5995
                </a>
              </div>

              <div className="bg-white/10 backdrop-blur-sm text-white rounded-2xl p-6 border-2 border-white/30">
                <div className="flex items-center gap-3 mb-2">
                  <Mail size={24} />
                  <p className="text-sm font-semibold">EMAIL US</p>
                </div>
                <a href="mailto:garagedoors306@gmail.com" className="text-xl font-bold hover:text-blue-200">
                  garagedoors306@gmail.com
                </a>
              </div>
            </div>

            <Button 
              onClick={() => window.location.href = '/'}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white font-bold text-lg px-12 py-7 shadow-2xl"
            >
              Get Your Free Quote Today
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;